/**
 * ProfileCard / ProfileRow — cbs-portal's stacked detail-card convention.
 *
 * A flat `rounded-xl border bg-card` section with a 12px uppercase title and
 * label/value rows on a fixed 132px label column. Empty values render an
 * em-dash. Shared by the account Overview, the internal VBA group detail, and
 * the member detail so the three screens use one implementation.
 */

import type {ReactElement, ReactNode} from 'react';
import {cn} from '@common-web/lib/utils';

export function ProfileCard({
  title,
  action,
  children,
  className,
}: {
  title: ReactNode;
  /** Optional right-aligned header affordance (edit button, pill, etc.). */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}): ReactElement {
  return (
    <section className={cn('rounded-xl border bg-card', className)}>
      <div className='flex items-center justify-between gap-2 border-b px-5 py-3'>
        <h3 className='text-[12px] font-semibold uppercase tracking-wider text-muted-foreground'>
          {title}
        </h3>
        {action}
      </div>
      <div className='px-5 py-2'>{children}</div>
    </section>
  );
}

function isEmptyValue(value: ReactNode): boolean {
  // Booleans count as empty: React renders them as nothing, so without this a
  // `value={false}` row would show blank instead of the documented em dash.
  return value === null || value === undefined || typeof value === 'boolean' || value === '';
}

export function ProfileRow({
  label,
  value,
  mono = false,
}: {
  label: ReactNode;
  value: ReactNode;
  /** Render the value in mono/tabular figures (IDs, numbers). */
  mono?: boolean;
}): ReactElement {
  return (
    <div className='grid grid-cols-[132px_1fr] items-baseline gap-3 py-2'>
      <span className='text-[12px] text-muted-foreground'>{label}</span>
      <span
        className={cn(
          'min-w-0 break-words text-sm text-foreground',
          mono && 'font-mono tabular-nums text-[13px]',
        )}
      >
        {isEmptyValue(value) ? <span className='text-muted-foreground'>—</span> : value}
      </span>
    </div>
  );
}

/**
 * Sub-block divider inside a ProfileCard (e.g. the "Primary Contact" block
 * within Identity): hairline top border + 11px uppercase heading.
 */
export function ProfileSubheading({children}: {children: ReactNode}): ReactElement {
  return (
    <div className='mt-2 border-t pt-3 pb-1'>
      <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
        {children}
      </p>
    </div>
  );
}
